import { Activity, BarChart3, Bot, Home, Settings, User, LogOut, Users } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { NotificationDropdown } from './NotificationDropdown'
import { cn } from '../utils/cn'

type NavChild = {
  name: string
  href: string
  match: (path: string) => boolean
}

type NavItem = {
  name: string
  href: string
  icon: typeof Home
  match: (path: string) => boolean
  children?: NavChild[]
}

const navigation: NavItem[] = [
  {
    name: 'Accueil',
    href: '/',
    icon: Home,
    match: (path) => path === '/',
  },
  {
    name: 'Modèles',
    href: '/teams',
    icon: Bot,
    match: (path) => path.startsWith('/agents') || path === '/teams' || path.startsWith('/teams/'),
    children: [
      { name: 'Équipes', href: '/teams', match: (path) => path === '/teams' },
      { name: 'Cercles', href: '/teams/circles', match: (path) => path.startsWith('/teams/circles') },
      { name: 'Ressources', href: '/teams/resources', match: (path) => path.startsWith('/teams/resources') },
      { name: 'Agents', href: '/agents', match: (path) => path.startsWith('/agents') },
    ],
  },
  {
    name: 'Instances',
    href: '/instances/equipes',
    icon: Activity,
    match: (path) => path.startsWith('/instances'),
    children: [
      { name: 'Équipes', href: '/instances/equipes', match: (path) => path.startsWith('/instances/equipes') },
      { name: 'Cercles', href: '/instances/cercles', match: (path) => path.startsWith('/instances/cercles') },
      { name: 'Agents', href: '/instances/agents', match: (path) => path.startsWith('/instances/agents') },
    ],
  },
  {
    name: 'Suivi',
    href: '/suivi/statistiques',
    icon: BarChart3,
    match: (path) => path.startsWith('/suivi'),
    children: [
      { name: 'Statistiques', href: '/suivi/statistiques', match: (path) => path.startsWith('/suivi/statistiques') },
      { name: 'Graphiques', href: '/suivi/graphiques', match: (path) => path.startsWith('/suivi/graphiques') },
    ],
  },
  {
    name: 'Paramètres',
    href: '/settings/profil',
    icon: Settings,
    match: (path) => path.startsWith('/settings'),
    children: [
      { name: 'Profil', href: '/settings/profil', match: (path) => path.startsWith('/settings/profil') },
      { name: 'Secrets', href: '/settings/secrets', match: (path) => path.startsWith('/settings/secrets') },
      { name: 'Admin', href: '/settings/admin', match: (path) => path.startsWith('/settings/admin') },
    ],
  },
  {
    name: 'Intelligence IntegrIA',
    href: '/team-inspector',
    icon: Users,
    match: (path) => path.startsWith('/team-inspector') || path.startsWith('/spending'),
    children: [
      { name: 'Inspecteur d\'équipe', href: '/team-inspector', match: (path) => path.startsWith('/team-inspector') },
      { name: 'Suivi des dépenses', href: '/spending', match: (path) => path.startsWith('/spending') },
    ],
  },
]

function sameSectionTarget(path: string, href: string, search: string): string | { pathname: string; search: string } {
  if (path.startsWith('/suivi') && href.startsWith('/suivi')) {
    return { pathname: href, search }
  }
  return href
}

export function Header() {
  const { user, logout } = useAuthStore()
  const location = useLocation()
  const path = location.pathname
  const current = navigation.find((item) => item.match(path))

  return (
    <header className="relative z-10 gradient-header">
      <div className="header-bar flex items-end justify-between gap-4 px-6 pt-2">
        <nav className="header-tabs flex items-end min-w-0 overflow-x-auto" aria-label="Sections">
          {navigation.map((item) => {
            const isActive = item.match(path)
            return (
              <Link
                key={item.name}
                to={sameSectionTarget(path, item.href, location.search)}
                className={cn('nav-tab', isActive && 'nav-tab--active')}
                aria-current={isActive ? 'page' : undefined}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.name}</span>
              </Link>
            )
          })}
        </nav>

        <div className="header-account flex items-center gap-3 pb-2 shrink-0">
          <div className="header-title">
            <img src="/logo.jpg" alt="" />
            <span className="text-sm font-semibold text-white">Studio</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center avatar-header">
              <User className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-medium hidden md:block text-white">
              {user?.name || user?.username || 'Utilisateur'}
            </span>
          </div>
          <button
            type="button"
            onClick={logout}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all button-header"
            title="Déconnexion"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden md:inline">Déconnexion</span>
          </button>
          <NotificationDropdown />
        </div>
      </div>

      {current?.children && (
        <nav className="nav-sub header-tabs flex items-end" aria-label={current.name}>
          {current.children.map((child) => {
            const isActive = child.match(path)
            return (
              <Link
                key={child.href}
                to={sameSectionTarget(path, child.href, location.search)}
                className={cn('nav-tab nav-tab--sub', isActive && 'nav-tab--active')}
                aria-current={isActive ? 'page' : undefined}
              >
                <span>{child.name}</span>
              </Link>
            )
          })}
        </nav>
      )}
    </header>
  )
}
